import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation, useSearchParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  Lock,
  AlertCircle,
  FlaskConical,
  ChevronRight,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getEventoVisibleByIdParaSocio,
  hidratarEventosPublicos,
} from "@/data/adminEventosStore";
import { getCurrentSocio } from "@/data/socioAuthStore";
import { registrarCompra, hidratarComprasBoletos, obtenerCompraBackend } from "@/data/comprasStore";
import {
  crearIntent,
  obtenerPago,
  esEstadoTerminal,
  isSimuladorHabilitado,
  nuevaIdempotencyKey,
  mensajeParaEstadoFallido,
  RESULTADOS_SIMULADOR,
} from "@/data/pagosStore";
import { PAGO, PAGO_AG } from "@/constants/testIds";
import MicroformFields from "@/screens/app/MicroformFields";
import Pago3DSFlow from "@/screens/app/Pago3DSFlow";
import { statusF2A, finalizeF2A } from "@/data/pagosF2aStore";
import { esBanbajio } from "@/data/cybersourceStore";

/**
 * Pantalla de Pago · Sprint 42 · Entrega 2.
 *
 * Flujo transaccional real contra el backend:
 *   1. POST /api/compras   → compra en PENDIENTE_DE_PAGO (sin boletos).
 *   2. POST /api/pagos/intent (idempotente) → dispara el proveedor.
 *   3. Polling GET /api/pagos/{id} hasta estado terminal (o cierre local).
 *   4. APROBADO → navega a CompraConfirmada (boletos ya creados por backend).
 *   5. Cualquier otro estado terminal → mensaje + botón Reintentar (NUEVA
 *      idempotencyKey, NUEVA compra, respetando disponibilidad en el backend).
 *
 * Idempotencia frontend:
 *   - Doble clic protegido por `inFlightRef`.
 *   - Reintento genera nuevas keys (compra + pago).
 *   - Cleanup: al desmontar se cancela el polling.
 *
 * El frontend NUNCA emite boletos, QR ni notificación por sí mismo. La
 * fuente de verdad es el backend (`services/pagos_finalizacion.py`).
 */

const UI_TIMEOUT_MS = 45_000;  // UX local · el backend expira formalmente a 10 min.
const POLL_INTERVAL_MS = 1500;

export default function Pago() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [hidratado, setHidratado] = useState(false);
  useEffect(() => {
    hidratarEventosPublicos().finally(() => setHidratado(true));
  }, []);
  const evento = getEventoVisibleByIdParaSocio(id);
  const seleccion = location.state;

  // Sprint 57 · F4B · Modo Acceso General: la Compra YA existe (creada en el
  // resumen multisalón). El pago único se crea contra esa Compra persistida.
  const compraQuery = searchParams.get("compra");
  const esAG =
    !!compraQuery ||
    location.state?.modalidad === "ACCESO_GENERAL" ||
    evento?.modalidad === "ACCESO_GENERAL";
  const [compraAG, setCompraAG] = useState(location.state?.compra || null);
  useEffect(() => {
    if (!esAG) return;
    if (compraAG && compraAG.id === compraQuery) return;
    if (compraQuery) {
      obtenerCompraBackend(compraQuery).then((c) => {
        if (c && c.id) setCompraAG(c);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAG, compraQuery]);

  // Sprint 59 · F2A · MESAS con 3DS/pago seguro: la compra pendiente se crea
  // ANTES del pago y se refleja en ?cmesa= para recuperación tras F5. Reutiliza
  // EXACTAMENTE el mismo servicio de compras y el mismo Pago3DSFlow (sin
  // implementación paralela).
  const compraMesaQuery = searchParams.get("cmesa");
  const [compraMesaId, setCompraMesaId] = useState(compraMesaQuery || null);
  const [compraMesaData, setCompraMesaData] = useState(null);
  const [creandoCompra, setCreandoCompra] = useState(false);
  // Sprint 59 · F2A · estado del intento de pago existente (recuperación F5).
  const [f2aEstado, setF2aEstado] = useState(null);
  // Guarda contra doble render/F5/dos pestañas: la reanudación de APPROVED se
  // dispara UNA sola vez mientras no confirme FINALIZED.
  const f2aResumeRef = useRef(false);
  useEffect(() => {
    if (esAG || !compraMesaQuery || compraMesaData) return;
    obtenerCompraBackend(compraMesaQuery).then((c) => {
      if (c && c.id) {
        setCompraMesaData(c);
        setCompraMesaId(c.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAG, compraMesaQuery]);

  const simulatorEnabled = useMemo(() => isSimuladorHabilitado(), []);
  // Sprint 59 · F1A · modo BanBajío (Flex Microform). Mock sigue por default.
  const banbajio = useMemo(() => esBanbajio(), []);
  const [resultado, setResultado] = useState("approved");
  const [fase, setFase] = useState("form"); // form | processing | fallido
  const [pago, setPago] = useState(null);
  const [mensajeFallo, setMensajeFallo] = useState(null);
  // Sprint 55 · error de NEGOCIO no reintentable (límite/disponibilidad/
  // venta pausada): repetir el pago no lo resuelve. Se muestra sin botón
  // "Reintentar" y con salidas seguras (evento / Mis Boletos).
  const [errorNegocio, setErrorNegocio] = useState(null);

  // Campos visuales de tarjeta (SOLO UI · no se persisten ni se envían).
  const [numero, setNumero] = useState("");
  const [titular, setTitular] = useState("");
  const [venc, setVenc] = useState("");
  const [cvv, setCvv] = useState("");
  // Sprint 59 · F1B · JWT transitorio COMPLETO (memoria) para arrancar 3DS.
  const [transientJwt, setTransientJwt] = useState(null);

  // Refs que sobreviven a re-renders para proteger idempotencia y polling.
  const inFlightRef = useRef(false);       // bloquea doble clic / dobles llamadas.
  const attemptRef = useRef({
    compraIdem: null,
    pagoIdem: null,
    compraId: null,
    pagoId: null,
    startedAt: 0,
  });
  // Sprint 54 · Fase A · Idempotencia REAL: una misma intención lógica de
  // compra (mismo evento + mesa + cantidad) conserva la MISMA idempotencyKey
  // durante doble clic, reintento por timeout, pérdida de red o recarga.
  // Solo una selección/compra distinta genera llaves nuevas.
  const purchaseIdemRef = useRef({ selKey: null, compraIdem: null, pagoIdem: null });
  const pollTimerRef = useRef(null);
  const desmontadoRef = useRef(false);

  useEffect(() => {
    // Reset on mount so React 18 StrictMode's double-invoke (mount → cleanup
    // → remount) doesn't leave `desmontado` stuck at true, which would block
    // the APROBADO navigation from `cerrarConTerminal`.
    desmontadoRef.current = false;
    return () => {
      desmontadoRef.current = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const socio = getCurrentSocio();

  // Sprint 54 · Fase A · Idempotencia: la pareja de llaves se genera ANTES
  // de la primera solicitud, en cuanto se conoce la selección. Así una
  // intención lógica (evento+mesa+cantidad) conserva UNA sola pareja de
  // llaves durante doble clic, re-render, StrictMode, timeout y reintento.
  useEffect(() => {
    if (esAG) return; // AG reutiliza una clave estable por Compra (ver iniciarPago).
    if (!evento || !seleccion) return;
    const selKey = `${evento.id}|${seleccion.mesa}|${seleccion.cantidad}`;
    if (
      purchaseIdemRef.current.selKey !== selKey ||
      !purchaseIdemRef.current.compraIdem
    ) {
      purchaseIdemRef.current = {
        selKey,
        compraIdem: nuevaIdempotencyKey("CMP"),
        pagoIdem: nuevaIdempotencyKey("PGO"),
      };
    }
  }, [evento, seleccion]);

  const detenerPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Aterriza el resultado terminal en UI y navegación.
  const cerrarConTerminal = useCallback(
    async (pagoTerminal) => {
      detenerPolling();
      inFlightRef.current = false;
      setPago(pagoTerminal);
      if (pagoTerminal.estado === "APROBADO") {
        // Boletos y notificación ya existen en el backend. Refrescamos
        // el cache local antes de navegar para que MisBoletos los muestre
        // de inmediato.
        try {
          await hidratarComprasBoletos();
        } catch {
          /* silencioso: MisBoletos volverá a hidratar al montarse. */
        }
        if (desmontadoRef.current) return;
        const compraIdConfirmada = attemptRef.current.compraId;
        navigate(
          compraIdConfirmada
            ? `/app/compra-confirmada/${compraIdConfirmada}`
            : "/app/mis-boletos",
          {
            replace: true,
            state: { pago: pagoTerminal },
          },
        );
        return;
      }
      // Estados fallidos: quedamos en Pago con mensaje + reintento.
      setFase("fallido");
      setMensajeFallo(mensajeParaEstadoFallido(pagoTerminal));
    },
    [navigate, detenerPolling],
  );

  // Bucle de polling ligero (setTimeout recursivo, no setInterval → evita
  // solapamiento cuando la respuesta se retrasa).
  const encolarPoll = useCallback(
    (pagoId) => {
      pollTimerRef.current = setTimeout(async () => {
        if (desmontadoRef.current) return;
        const p = await obtenerPago(pagoId);
        if (desmontadoRef.current) return;
        if (p) {
          setPago(p);
          if (esEstadoTerminal(p.estado)) {
            cerrarConTerminal(p);
            return;
          }
        }
        // UX timeout local: no bloqueamos indefinidamente al usuario.
        const transcurrido = Date.now() - attemptRef.current.startedAt;
        if (transcurrido > UI_TIMEOUT_MS) {
          detenerPolling();
          inFlightRef.current = false;
          setFase("fallido");
          setMensajeFallo(
            "El pago está tardando más de lo esperado. Puedes reintentar; " +
              "si el banco confirma después, tu compra aparecerá en Mis Boletos.",
          );
          return;
        }
        encolarPoll(pagoId);
      }, POLL_INTERVAL_MS);
    },
    [cerrarConTerminal, detenerPolling],
  );

  const iniciarPago = useCallback(async () => {
    if (inFlightRef.current) return; // Idempotencia frontend: doble clic.
    if (!evento || !seleccion || !socio) return;
    inFlightRef.current = true;
    setMensajeFallo(null);
    setErrorNegocio(null);
    setFase("processing");

    // Idempotencia REAL: la MISMA intención (evento+mesa+cantidad) reusa
    // la misma pareja de llaves; una selección distinta genera llaves nuevas.
    const selKey = `${evento.id}|${seleccion.mesa}|${seleccion.cantidad}`;
    if (purchaseIdemRef.current.selKey !== selKey) {
      purchaseIdemRef.current = {
        selKey,
        compraIdem: nuevaIdempotencyKey("CMP"),
        pagoIdem: nuevaIdempotencyKey("PGO"),
      };
    }
    const compraIdem = purchaseIdemRef.current.compraIdem;
    const pagoIdem = purchaseIdemRef.current.pagoIdem;
    attemptRef.current = {
      compraIdem,
      pagoIdem,
      compraId: null,
      pagoId: null,
      startedAt: Date.now(),
    };

    // 1) Crear compra en PENDIENTE_DE_PAGO. El backend re-valida
    //    disponibilidad de la mesa; si no hay lugar, devuelve 409.
    const compra = await registrarCompra({
      idempotencyKey: compraIdem,
      eventoId: evento.id,
      mesa: seleccion.mesa,
      cantidad: seleccion.cantidad,
      precioPorBoleto: seleccion.precioNum || seleccion.precio || 0,
      subtotal: seleccion.total,
      cargoServicio: 0,
      total: seleccion.total,
      moneda: "MXN",
    });
    if (!compra || compra.error) {
      inFlightRef.current = false;
      const reason = compra?.error;
      const NO_REINTENTABLE = [
        "LIMITE_ALCANZADO",
        "SIN_DISPONIBILIDAD",
        "VENTA_PAUSADA",
        "EVENTO_CERRADO",
      ];
      if (NO_REINTENTABLE.includes(reason)) {
        setErrorNegocio({ reason, mensaje: compra.mensaje || "" });
        setFase("fallido");
        return;
      }
      setFase("fallido");
      setMensajeFallo(
        compra?.error === "network"
          ? "No pudimos conectarnos al servidor. Verifica tu conexión e intenta de nuevo."
          : `No pudimos preparar tu compra${compra?.mensaje ? `: ${compra.mensaje}` : ""}. Intenta nuevamente.`,
      );
      return;
    }
    attemptRef.current.compraId = compra.id;

    // 2) Crear intent contra el proveedor. En dev enviamos el escenario
    //    seleccionado; en prod el saneador del backend lo descarta.
    const intent = await crearIntent({
      idempotencyKey: pagoIdem,
      compraId: compra.id,
      socioId: socio.id || socio.numeroSocio,
      eventoId: evento.id,
      monto: seleccion.total,
      moneda: "MXN",
      metadatos: simulatorEnabled ? { mockResult: resultado } : null,
    });
    if (!intent || intent.error) {
      inFlightRef.current = false;
      setFase("fallido");
      setMensajeFallo(
        `No pudimos iniciar el pago${intent?.error ? ` (${intent.error})` : ""}. Intenta nuevamente.`,
      );
      return;
    }
    attemptRef.current.pagoId = intent.id;
    setPago(intent);

    // 3) Si el backend devolvió estado terminal inmediato (mock aprobado /
    //    rechazado / cancelado / error), aterrizamos sin polling.
    if (esEstadoTerminal(intent.estado)) {
      cerrarConTerminal(intent);
      return;
    }
    // 4) Estado PROCESANDO → polling ligero.
    encolarPoll(intent.id);
  }, [
    evento,
    seleccion,
    socio,
    resultado,
    simulatorEnabled,
    encolarPoll,
    cerrarConTerminal,
  ]);

  // Sprint 57 · F4B · Pago único para Compra ACCESO_GENERAL ya persistida.
  // No crea la Compra (ya existe): sólo crea el intent por el TOTAL
  // autoritativo de la Compra. La clave es estable por compra → doble clic
  // y reintento de red reutilizan el mismo intent (idempotente).
  const iniciarPagoAG = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!evento || !compraAG || !socio) return;
    inFlightRef.current = true;
    setMensajeFallo(null);
    setErrorNegocio(null);
    setFase("processing");

    const pagoIdem = `PGO-${compraAG.id}`;
    attemptRef.current = {
      compraIdem: null,
      pagoIdem,
      compraId: compraAG.id,
      pagoId: null,
      startedAt: Date.now(),
    };

    const intent = await crearIntent({
      idempotencyKey: pagoIdem,
      compraId: compraAG.id,
      socioId: socio.id || socio.numeroSocio,
      eventoId: evento.id,
      // Monto por referencia; el backend IGNORA el importe del cliente y usa
      // el total autoritativo de la Compra persistida.
      monto: Number(compraAG.total || 0),
      moneda: "MXN",
      metadatos: simulatorEnabled ? { mockResult: resultado } : null,
    });

    if (!intent || intent.error) {
      inFlightRef.current = false;
      const errObj = intent?.error;
      const reason = errObj && typeof errObj === "object" ? errObj.reason : errObj;
      const mensaje = errObj && typeof errObj === "object" ? errObj.detail : "";
      const NO_REINTENTABLE = [
        "RESERVA_LIBERADA",
        "LIMITE_ALCANZADO",
        "EVENTO_CERRADO",
        "SIN_DISPONIBILIDAD",
        "VENTA_PAUSADA",
      ];
      if (NO_REINTENTABLE.includes(reason)) {
        setErrorNegocio({ reason, mensaje: mensaje || "" });
        setFase("fallido");
        return;
      }
      setFase("fallido");
      setMensajeFallo(
        reason === "network_error" || !reason
          ? "No pudimos iniciar el pago. Verifica tu conexión e intenta de nuevo."
          : `No pudimos iniciar el pago (${reason}). Intenta nuevamente.`,
      );
      return;
    }
    attemptRef.current.pagoId = intent.id;
    setPago(intent);
    if (esEstadoTerminal(intent.estado)) {
      cerrarConTerminal(intent);
      return;
    }
    encolarPoll(intent.id);
  }, [
    evento,
    compraAG,
    socio,
    resultado,
    simulatorEnabled,
    encolarPoll,
    cerrarConTerminal,
  ]);


  // Sprint 59 · F2A · MESAS · crea la compra pendiente (mismo servicio que el
  // flujo mock) para poder iniciar Microform + 3DS + pago seguro. El backend
  // re-valida disponibilidad de la mesa; total/cantidad son autoritativos.
  const prepararCompraMesa = useCallback(async () => {
    if (!evento || !seleccion || !socio || creandoCompra) return;
    setCreandoCompra(true);
    setMensajeFallo(null);
    setErrorNegocio(null);
    const selKey = `${evento.id}|${seleccion.mesa}|${seleccion.cantidad}`;
    if (purchaseIdemRef.current.selKey !== selKey || !purchaseIdemRef.current.compraIdem) {
      purchaseIdemRef.current = {
        selKey,
        compraIdem: nuevaIdempotencyKey("CMP"),
        pagoIdem: nuevaIdempotencyKey("PGO"),
      };
    }
    const compra = await registrarCompra({
      idempotencyKey: purchaseIdemRef.current.compraIdem,
      eventoId: evento.id,
      mesa: seleccion.mesa,
      cantidad: seleccion.cantidad,
      precioPorBoleto: seleccion.precioNum || seleccion.precio || 0,
      subtotal: seleccion.total,
      cargoServicio: 0,
      total: seleccion.total,
      moneda: "MXN",
    });
    setCreandoCompra(false);
    if (!compra || compra.error) {
      const reason = compra?.error;
      const NO_REINTENTABLE = ["LIMITE_ALCANZADO", "SIN_DISPONIBILIDAD", "VENTA_PAUSADA", "EVENTO_CERRADO"];
      if (NO_REINTENTABLE.includes(reason)) {
        setErrorNegocio({ reason, mensaje: compra?.mensaje || "" });
        setFase("fallido");
        return;
      }
      setMensajeFallo("No pudimos preparar tu compra. Intenta nuevamente. No se realizó ningún cargo.");
      return;
    }
    setCompraMesaData(compra);
    setCompraMesaId(compra.id);
    // ?cmesa= permite recuperar el mismo intento tras F5 (no crea otra compra).
    setSearchParams({ cmesa: compra.id }, { replace: true });
  }, [evento, seleccion, socio, creandoCompra, setSearchParams]);

  const reintentar = useCallback(() => {
    // Sprint 54 · Reintento de la MISMA intención (mismo evento+mesa+cantidad):
    // conserva la idempotencyKey (en purchaseIdemRef) para que el backend
    // deduplique y NO se dupliquen compra/pago. Solo limpiamos el estado del
    // intento en vuelo. Una selección distinta generará llaves nuevas.
    detenerPolling();
    inFlightRef.current = false;
    attemptRef.current = {
      compraIdem: null,
      pagoIdem: null,
      compraId: null,
      pagoId: null,
      startedAt: 0,
    };
    setPago(null);
    setMensajeFallo(null);
    setErrorNegocio(null);
    setFase("form");
  }, [detenerPolling]);

  // Sprint 59 · F2A · Recuperación tras F5 ANTES de iniciar 3DS: si ya existe
  // un intento de pago para esta compra, consultamos su estado y bloqueamos el
  // reinicio del pago (UNKNOWN/PROCESSING/APPROVED) o navegamos si FINALIZED.
  useEffect(() => {
    const cid = esAG ? compraAG?.id : compraMesaId;
    if (!banbajio || !cid || transientJwt) return;
    statusF2A(cid).then((st) => {
      if (st && !st.error && st.estado) setF2aEstado(st.estado);
    });
  }, [banbajio, esAG, compraAG, compraMesaId, transientJwt]);
  // Sprint 59 · F2A · Recuperación de APPROVED tras F5/caída: dispara la
  // reanudación LOCAL de la finalización (idempotente, SIN reenviar el pago).
  // Aplica igual a ACCESO_GENERAL (?compra=) y MESAS (?cmesa=). El guardado
  // f2aResumeRef evita dobles llamadas por doble render/F5/dos pestañas.
  useEffect(() => {
    const cid = esAG ? compraAG?.id : compraMesaId;
    if (!banbajio || !cid || transientJwt) return;
    if (f2aEstado !== "APPROVED" || f2aResumeRef.current) return;
    f2aResumeRef.current = true;
    finalizeF2A(cid).then((st) => {
      if (st && !st.error && st.estado) setF2aEstado(st.estado);
      // Si aún no finalizó (emisión pendiente), permitir un nuevo intento.
      if (!st || st.error || st.estado !== "FINALIZED") f2aResumeRef.current = false;
    });
  }, [banbajio, esAG, compraAG, compraMesaId, transientJwt, f2aEstado]);
  useEffect(() => {
    const cid = esAG ? compraAG?.id : compraMesaId;
    if (f2aEstado === "FINALIZED" && cid) {
      navigate(`/app/compra-confirmada/${cid}`, { replace: true });
    }
  }, [f2aEstado, esAG, compraAG, compraMesaId, navigate]);

  if (!evento) {
    if (!hidratado) return null;
    return <Navigate to="/app" replace />;
  }
  if (esAG) {
    // AG · sin compra en state ni query → volver a la selección de salones.
    if (!compraQuery && !location.state?.compra) {
      return <Navigate to={`/app/eventos/${id}/acceso-general`} replace />;
    }
    if (!compraAG) {
      // Recuperando la Compra desde backend (recarga F5 / pestaña nueva).
      return (
        <div
          data-testid={PAGO.screen}
          className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground"
        >
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando tu compra…
        </div>
      );
    }
  } else if (!seleccion && !compraMesaQuery) {
    return <Navigate to={`/app/eventos/${id}/seleccion-mesa`} replace />;
  } else if (!seleccion && compraMesaQuery && !compraMesaData) {
    return (
      <div
        data-testid={PAGO.screen}
        className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground"
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando tu compra…
      </div>
    );
  }

  // Recap de MESAS: usa la selección en sesión o, tras F5, la compra del backend.
  const mesaSel = seleccion || compraMesaData || {};
  const mesaNum = mesaSel.mesa;
  const cantMesa = seleccion ? seleccion.cantidad : compraMesaData?.cantidad;
  const precioMesa = seleccion ? seleccion.precio : `$${Number(compraMesaData?.precioPorBoleto || 0).toLocaleString("es-MX")} MXN`;
  const totalMesa = seleccion ? seleccion.total : Number(compraMesaData?.total || 0);

  const { mesa, cantidad, precio, total } = seleccion || {};
  const fmt = (n) => `$${Number(n).toLocaleString("es-MX")} MXN`;
  // Total y desglose AUTORITATIVOS desde la Compra persistida (nunca del carrito).
  const itemsAG = esAG && compraAG ? compraAG.items || [] : [];
  const totalAG = esAG && compraAG ? Number(compraAG.total || 0) : 0;
  const totalBoletosAG = itemsAG.reduce((s, it) => s + Number(it.cantidad || 0), 0);
  const montoMostrado = esAG ? totalAG : totalMesa;
  // Cantidad y total AUTORITATIVOS de la compra persistida (backend). El
  // frontend nunca los calcula ni los puede modificar.
  const cantidadAutoritativa = esAG ? totalBoletosAG : Number(cantMesa || 0);
  const compraInconsistente = Number(montoMostrado || 0) > 0 && cantidadAutoritativa <= 0;
  // Sprint 59 · F2A · compraId unificado para Microform+3DS+pago (AG o MESAS).
  const compraId3ds = esAG ? compraAG?.id : compraMesaId;

  // ─── Vista PROCESSING ─────────────────────────────────────────────────

  if (fase === "processing") {
    const estadoLegible = pago?.estado || "PROCESANDO";
    return (
      <div
        data-testid={PAGO.procesando}
        className="min-h-[60vh] flex flex-col items-center justify-center gap-6 text-center"
      >
        <div className="w-16 h-16 grid place-items-center rounded-2xl bg-accent/15 text-accent">
          <Loader2 className="w-7 h-7 animate-spin" strokeWidth={1.6} />
        </div>
        <div>
          <span className="overline text-accent">Procesando</span>
          <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-foreground">
            Estamos procesando tu pago
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-md">
            No cierres esta ventana. Te llevaremos a la confirmación
            automáticamente cuando el banco responda.
          </p>
        </div>
        <div
          data-testid={PAGO.procesandoEstado}
          className="rounded-full border hairline bg-card px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground"
        >
          {estadoLegible}
        </div>
      </div>
    );
  }

  // ─── Vista FALLIDO NO REINTENTABLE (límite/disponibilidad/pausada) ────

  if (fase === "fallido" && errorNegocio) {
    const titulo =
      errorNegocio.reason === "LIMITE_ALCANZADO"
        ? "Alcanzaste el límite de boletos"
        : errorNegocio.reason === "SIN_DISPONIBILIDAD"
        ? "Los lugares ya no están disponibles"
        : errorNegocio.reason === "RESERVA_LIBERADA"
        ? "Tu reserva expiró"
        : errorNegocio.reason === "EVENTO_CERRADO"
        ? "El evento ya está cerrado"
        : errorNegocio.reason === "VENTA_PAUSADA"
        ? "La venta está pausada"
        : "No es posible completar esta compra";
    const tid =
      errorNegocio.reason === "LIMITE_ALCANZADO"
        ? PAGO.msgLimite
        : PAGO.msgNoReintentable;
    return (
      <div className="flex flex-col gap-6 pb-8">
        <header className="animate-fade-up">
          <span className="overline text-destructive">Compra no permitida</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
            {titulo}
          </h1>
        </header>

        <div
          data-testid={tid}
          role="alert"
          className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-5 flex items-start gap-3 animate-fade-up"
        >
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
            <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <p className="flex-1 text-sm text-foreground leading-relaxed">
            {errorNegocio.mensaje ||
              "Esta compra no puede completarse. No se realizó ningún cargo."}
          </p>
        </div>

        <p className="text-xs text-muted-foreground">
          No se realizó ningún cargo y no se generaron boletos. Reintentar el
          pago no resolvería esto.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid={PAGO.btnVolverEvento}
            onClick={() => navigate(`/app/eventos/${evento.id}`)}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
          >
            Volver al evento
          </button>
          <button
            type="button"
            data-testid={PAGO.btnMisBoletos}
            onClick={() => navigate("/app/mis-boletos")}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-95 active:scale-[0.99] transition-all"
          >
            Ir a Mis Boletos
          </button>
        </div>
      </div>
    );
  }

  // ─── Vista FALLIDO (RECHAZADO/ERROR/CANCELADO/EXPIRADO/UI-timeout) ────

  if (fase === "fallido") {
    const estadoTid =
      pago?.estado === "RECHAZADO"
        ? PAGO.msgRechazado
        : pago?.estado === "CANCELADO"
        ? PAGO.msgCancelado
        : pago?.estado === "EXPIRADO"
        ? PAGO.msgExpirado
        : pago?.estado === "ERROR_COMUNICACION"
        ? PAGO.msgError
        : PAGO.msgFallido;

    return (
      <div className="flex flex-col gap-6 pb-8">
        <header className="animate-fade-up">
          <span className="overline text-destructive">Pago no completado</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
            No pudimos completar el pago
          </h1>
        </header>

        <div
          data-testid={estadoTid}
          role="alert"
          className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-5 flex items-start gap-3 animate-fade-up"
        >
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
            <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <div className="flex-1">
            <p className="text-sm text-foreground leading-relaxed">
              {mensajeFallo}
            </p>
            {pago?.estado && (
              <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
                Estado del pago: {pago.estado}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          No se realizó ningún cargo. No se generaron boletos ni códigos QR. Si
          conservas tu turno de Smart Queue, puedes reintentar con el tiempo
          restante.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid={PAGO.btnReintentar}
            onClick={reintentar}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-95 active:scale-[0.99] transition-all"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={2} />
            Reintentar el pago
          </button>
          <button
            type="button"
            data-testid={PAGO.btnVolverEventos}
            onClick={() => navigate("/app")}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // ─── Vista FORM (default) ─────────────────────────────────────────────

  return (
    <div data-testid={PAGO.screen} className="flex flex-col gap-6 pb-8">
      <button
        type="button"
        data-testid={PAGO.btnRegresar}
        onClick={() =>
          esAG
            ? navigate(`/app/eventos/${evento.id}/acceso-general`)
            : navigate(`/app/eventos/${evento.id}/resumen`, { state: seleccion })
        }
        aria-label="Regresar"
        className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </span>
        Regresar
      </button>

      <header className="animate-fade-up">
        <span className="overline text-accent">Pago</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          Confirma tu compra
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          <Lock className="inline w-3.5 h-3.5 mr-1 text-accent align-[-2px]" strokeWidth={1.7} />
          Los datos de tarjeta no se almacenan.
        </p>
      </header>

      <section
        data-testid={PAGO.recap}
        className="rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft flex flex-col gap-4"
      >
        {esAG ? (
          <>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col text-sm min-w-0">
                <span className="font-heading text-base text-foreground truncate">
                  {evento.nombre}
                </span>
                <span
                  data-testid={PAGO_AG.modalidad}
                  className="text-muted-foreground mt-1 inline-flex items-center gap-1.5"
                >
                  Acceso general ·{" "}
                  <span data-testid={PAGO_AG.totalBoletos}>
                    {totalBoletosAG} {totalBoletosAG === 1 ? "boleto" : "boletos"}
                  </span>
                </span>
              </div>
              <div className="text-right shrink-0">
                <span className="overline text-muted-foreground">Total</span>
                <p
                  data-testid={PAGO.total}
                  className="font-heading text-xl text-primary dark:text-accent mt-1"
                >
                  {fmt(montoMostrado)}
                </p>
              </div>
            </div>
            <ul data-testid={PAGO_AG.desglose} className="flex flex-col border-t hairline pt-3 gap-2">
              {itemsAG.map((it) => (
                <li
                  key={it.id || it.salonAccesoId}
                  data-testid={PAGO_AG.item(it.salonAccesoId)}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-muted-foreground min-w-0 truncate">
                    {it.salon} · {it.cantidad} × {fmt(it.precioPorBoleto)}
                  </span>
                  <span className="text-foreground tabular-nums whitespace-nowrap">
                    {fmt(it.subtotal)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col text-sm">
              <span className="font-heading text-base text-foreground">
                {evento.nombre}
              </span>
              <span className="text-muted-foreground mt-1" data-testid="pago-mesa-recap">
                Mesa {mesaNum} · {cantMesa} {cantMesa === 1 ? "boleto" : "boletos"} · {precioMesa}
              </span>
            </div>
            <div className="text-right shrink-0">
              <span className="overline text-muted-foreground">Total</span>
              <p
                data-testid={PAGO.total}
                className="font-heading text-xl text-primary dark:text-accent mt-1"
              >
                {fmt(totalMesa)}
              </p>
            </div>
          </div>
        )}
      </section>

      {compraInconsistente ? (
        <section
          data-testid="pago-compra-inconsistente"
          role="alert"
          className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-6 shadow-soft flex items-start gap-3"
        >
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
            <CreditCard className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <p className="text-sm text-foreground">
            No podemos continuar con el pago: la compra no tiene boletos válidos.
            Vuelve a iniciar la selección. No se realizó ningún cargo.
          </p>
        </section>
      ) : banbajio ? (
        <div className="flex flex-col gap-6">
          {!compraId3ds ? (
            <section className="rounded-2xl border hairline bg-card p-6 shadow-soft flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                Prepara tu compra para continuar con el pago seguro. Aún no se
                realiza ningún cargo.
              </p>
              <button
                type="button"
                data-testid="tds-preparar-compra-mesa"
                onClick={prepararCompraMesa}
                disabled={creandoCompra}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all active:scale-[0.99] hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {creandoCompra ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Preparando…
                  </>
                ) : (
                  "Continuar al pago seguro"
                )}
              </button>
            </section>
          ) : f2aEstado === "UNKNOWN" ? (
            <section
              data-testid="tds-pago-desconocido"
              role="alert"
              className="rounded-2xl border hairline bg-amber-500/5 dark:bg-amber-500/10 p-6 shadow-soft"
            >
              <p className="text-sm text-foreground">
                No pudimos confirmar el resultado del pago. NO intentes pagar de
                nuevo: revisaremos el estado y, si se cobró, aparecerá en Mis
                Boletos.
              </p>
            </section>
          ) : f2aEstado === "PROCESSING" || f2aEstado === "APPROVED" ? (
            <section
              data-testid="tds-pago-procesando"
              className="rounded-2xl border hairline bg-card p-6 shadow-soft flex items-center gap-3"
            >
              <Loader2 className="w-4 h-4 animate-spin text-accent" />
              <p className="text-sm text-muted-foreground">Estamos confirmando tu pago…</p>
            </section>
          ) : (
            <>
              <MicroformFields
                compraId={compraId3ds}
                disabled={fase === "processing"}
                onToken={(_mask, transientJwtVal) => {
                  if (transientJwtVal) setTransientJwt(transientJwtVal);
                }}
                onError={(m) => setMensajeFallo(m)}
              />
              {transientJwt && compraId3ds && (
                <Pago3DSFlow
                  compraId={compraId3ds}
                  transientJwt={transientJwt}
                  onFinalized={(cid) => navigate(`/app/compra-confirmada/${cid}`, { replace: true })}
                />
              )}
            </>
          )}
        </div>
      ) : (
      <section className="rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft animate-fade-up delay-100">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-accent/10 text-accent">
            <CreditCard className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <h2 className="font-heading text-lg text-foreground">
            Datos de la tarjeta
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-2 sm:col-span-2">
            <span className="text-xs text-muted-foreground">Número de tarjeta</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="•••• •••• •••• ••••"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
            />
          </label>
          <label className="flex flex-col gap-2 sm:col-span-2">
            <span className="text-xs text-muted-foreground">Titular</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="Como aparece en la tarjeta"
              value={titular}
              onChange={(e) => setTitular(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">Vencimiento</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="MM/AA"
              value={venc}
              onChange={(e) => setVenc(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">CVV</span>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              placeholder="•••"
              value={cvv}
              onChange={(e) => setCvv(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-background border hairline text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
            />
          </label>
        </div>
      </section>
      )}

      {simulatorEnabled && !banbajio && (
        <section
          data-testid={PAGO.simulador}
          className="rounded-2xl border-2 border-dashed border-amber-500/50 bg-amber-500/5 dark:bg-amber-500/10 p-5 md:p-6 animate-fade-up delay-200"
        >
          <div className="flex items-start gap-3 mb-4">
            <span className="w-9 h-9 grid place-items-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300">
              <FlaskConical className="w-4 h-4" strokeWidth={1.7} />
            </span>
            <div className="flex flex-col">
              <span className="overline text-amber-700 dark:text-amber-300">
                Simulador de pago — Solo desarrollo
              </span>
              <p className="mt-1 text-xs text-muted-foreground max-w-md">
                Selecciona un resultado para probar el flujo real. Estos
                controles se ocultan automáticamente en producción.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {RESULTADOS_SIMULADOR.map((r) => (
              <label
                key={r.id}
                data-testid={PAGO.simuladorOpcion(r.id)}
                className={cn(
                  "flex items-start gap-3 rounded-xl border hairline p-3 cursor-pointer transition-colors",
                  resultado === r.id
                    ? "bg-card ring-2 ring-amber-500/50"
                    : "bg-background/60 hover:bg-card",
                )}
              >
                <input
                  type="radio"
                  name="sim-resultado"
                  value={r.id}
                  checked={resultado === r.id}
                  onChange={() => setResultado(r.id)}
                  className="mt-1 accent-amber-500"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {r.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.descripcion}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </section>
      )}

      {!banbajio && !compraInconsistente && (
      <button
        type="button"
        data-testid={PAGO.btnPagar}
        onClick={esAG ? iniciarPagoAG : iniciarPago}
        disabled={inFlightRef.current || fase === "processing"}
        className={cn(
          "w-full inline-flex items-center justify-center gap-2 rounded-full",
          "px-6 py-4 text-sm font-semibold uppercase tracking-wide",
          "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground",
          "ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft",
          "transition-all duration-200 active:scale-[0.99] hover:opacity-95",
          "disabled:opacity-70 disabled:cursor-not-allowed",
        )}
      >
        Pagar {fmt(montoMostrado)}
        <ChevronRight className="w-4 h-4" strokeWidth={2} />
      </button>
      )}
    </div>
  );
}
